/**
 * 插單管理組件 | Rush Order Management Component
 * 顯示所有插單記錄，支援查看詳情、狀態追蹤
 */

import { useState, useMemo } from 'react';
import { useOrderStore } from '@/store';
import type { RushOrder } from '@/types/person';

type ViewType = 'list' | 'statistics';

export function RushOrderManagement() {
  const [viewType, setViewType] = useState<ViewType>('list');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  const { rushOrders, updateRushOrder } = useOrderStore();

  // 過濾插單
  const filteredOrders = useMemo(() => {
    if (selectedStatus === 'all') return rushOrders;
    return rushOrders.filter((order) => order.status === selectedStatus);
  }, [rushOrders, selectedStatus]);

  // 統計資料
  const statistics = useMemo(() => {
    const total = rushOrders.length;
    const pending = rushOrders.filter((o) => o.status === '待確認').length;
    const approved = rushOrders.filter((o) => o.status === '已接受').length;
    const rejected = rushOrders.filter((o) => o.status === '已拒絕').length;

    // 計算平均處理天數
    const completedOrders = rushOrders.filter((o) => o.status === '已接受');
    const avgProcessingDays =
      completedOrders.length > 0
        ? completedOrders.reduce((sum, order) => {
            const requested = new Date(order.requestedDeliveryDate);
            const original = new Date(order.originalDeliveryDate);
            return sum + Math.abs((requested.getTime() - original.getTime()) / (1000 * 60 * 60 * 24));
          }, 0) / completedOrders.length
        : 0;

    return {
      total,
      pending,
      approved,
      rejected,
      avgProcessingDays: Math.round(avgProcessingDays * 10) / 10,
    };
  }, [rushOrders]);

  // 獲取狀態配置
  const getStatusConfig = (status: RushOrder['status']) => {
    switch (status) {
      case '已接受':
        return {
          bg: 'bg-gradient-to-r from-green-100 to-emerald-100',
          text: 'text-green-700',
          border: 'border-green-300',
          dot: 'bg-green-500',
        };
      case '待確認':
        return {
          bg: 'bg-gradient-to-r from-blue-100 to-cyan-100',
          text: 'text-blue-700',
          border: 'border-blue-300',
          dot: 'bg-blue-500',
        };
      case '已拒絕':
        return {
          bg: 'bg-gradient-to-r from-red-100 to-rose-100',
          text: 'text-red-700',
          border: 'border-red-300',
          dot: 'bg-red-500',
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-amber-100 to-orange-100',
          text: 'text-amber-700',
          border: 'border-amber-300',
          dot: 'bg-amber-500',
        };
    }
  };

  // 獲取優先級配置
  const getPriorityConfig = (priority: RushOrder['priority']) => {
    return priority === '緊急'
      ? {
          bg: 'bg-gradient-to-r from-red-500 to-rose-500',
          text: 'text-white',
        }
      : {
          bg: 'bg-gradient-to-r from-amber-500 to-orange-500',
          text: 'text-white',
        };
  };

  // 格式化日期
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // 計算交期壓縮天數
  const calculateCompressionDays = (order: RushOrder) => {
    const requested = new Date(order.requestedDeliveryDate);
    const original = new Date(order.originalDeliveryDate);
    const diff = Math.abs((requested.getTime() - original.getTime()) / (1000 * 60 * 60 * 24));
    return Math.round(diff);
  };

  return (
    <div className="space-y-4">
      {/* 標題與控制列 */}
      <div className="bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-200 rounded-xl p-5 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* 左側：標題 */}
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg shadow-rose-500/30">
              <span className="text-white text-lg">⚡</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">插單管理</h2>
              <p className="text-sm text-slate-600">緊急訂單插單記錄與追蹤</p>
            </div>
          </div>

          {/* 右側：視圖切換 */}
          <div className="flex bg-white border-2 border-rose-200 rounded-lg shadow-sm overflow-hidden">
            {[
              { value: 'list', label: '插單列表', icon: '📋' },
              { value: 'statistics', label: '統計分析', icon: '📊' },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => setViewType(tab.value as ViewType)}
                className={`px-4 py-2 text-sm font-semibold transition-all flex items-center gap-2 ${
                  viewType === tab.value
                    ? 'bg-gradient-to-r from-rose-600 to-pink-500 text-white'
                    : 'text-rose-700 hover:bg-rose-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* 過濾器與統計 */}
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          {/* 狀態過濾器 */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-semibold text-slate-700">狀態：</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-1.5 bg-white border-2 border-rose-200 text-rose-700 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-rose-400 shadow-sm transition-all duration-200"
            >
              <option value="all">全部狀態</option>
              <option value="待確認">待確認</option>
              <option value="已接受">已接受</option>
              <option value="已拒絕">已拒絕</option>
            </select>
          </div>

          {/* 統計資訊 */}
          <div className="flex items-center gap-3 ml-auto flex-wrap">
            <div className="px-3 py-1.5 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-300 rounded-lg">
              <span className="text-xs font-bold text-rose-700">總插單 {statistics.total} 筆</span>
            </div>
            <div className="px-3 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-lg">
              <span className="text-xs font-bold text-amber-700">待確認 {statistics.pending} 筆</span>
            </div>
            <div className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-300 rounded-lg">
              <span className="text-xs font-bold text-blue-700">已接受 {statistics.approved} 筆</span>
            </div>
            <div className="px-3 py-1.5 bg-gradient-to-r from-red-50 to-rose-50 border border-red-300 rounded-lg">
              <span className="text-xs font-bold text-red-700">已拒絕 {statistics.rejected} 筆</span>
            </div>
          </div>
        </div>
      </div>

      {/* 內容區域 */}
      {viewType === 'list' && (
        <div className="bg-white border-2 border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {/* 標題列 */}
          <div className="bg-gradient-to-r from-slate-50 to-white border-b-2 border-slate-200 px-6 py-4">
            <h3 className="text-lg font-bold text-slate-800">插單列表</h3>
          </div>

          {/* 插單表格 */}
          {filteredOrders.length === 0 ? (
            <div className="bg-gradient-to-br from-slate-50 to-white p-12 text-center">
              <div className="text-6xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">暫無插單記錄</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                {rushOrders.length === 0
                  ? '目前沒有插單記錄，點擊「新增插單」按鈕建立第一筆急單'
                  : '沒有符合過濾條件的插單記錄，請嘗試調整過濾器'}
              </p>
              <div className="flex items-center justify-center gap-3">
                {selectedStatus !== 'all' ? (
                  <button
                    onClick={() => setSelectedStatus('all')}
                    className="px-5 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 hover:from-slate-200 hover:to-slate-300 text-slate-700 rounded-lg transition-all text-sm font-semibold shadow-sm active:scale-95"
                  >
                    清除過濾
                  </button>
                ) : rushOrders.length === 0 ? (
                  <div className="text-center">
                    <p className="text-sm text-slate-500 mb-2">請前往儀表板新增插單</p>
                    <button
                      onClick={() => window.location.href = '#rush'}
                      className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg transition-all text-sm font-semibold shadow-lg shadow-amber-500/30 active:scale-95 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      前往儀表板
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" role="table" aria-label="插單列表表格">
                <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">插單編號</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">產品</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">客戶</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">數量</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">優先級</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">原交期</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">要求交期</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">壓縮天數</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">狀態</th>
                    <th scope="col" className="px-4 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredOrders.map((order) => {
                    const statusConfig = getStatusConfig(order.status);
                    const priorityConfig = getPriorityConfig(order.priority);
                    const compressionDays = calculateCompressionDays(order);

                    return (
                      <tr key={order.id} className="hover:bg-rose-50/50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-mono font-bold text-rose-600">{order.id}</span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-slate-700">{order.productName}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{order.customer}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-slate-700">
                          {order.totalQuantity.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-bold ${priorityConfig.bg} ${priorityConfig.text} shadow-sm`}
                          >
                            {order.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                          {formatDate(order.originalDeliveryDate)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                          {formatDate(order.requestedDeliveryDate)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-bold text-rose-600">-{compressionDays} 天</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold shadow-sm border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`}></span>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            {order.status === '待確認' && (
                              <>
                                <button
                                  onClick={() => updateRushOrder(order.id, { status: '已接受' as RushOrder['status'] })}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                                  title="核准此插單"
                                  aria-label="核准插單"
                                >
                                  <span aria-hidden="true">✓</span>
                                  <span className="sr-only">核准</span>
                                </button>
                                <button
                                  onClick={() => updateRushOrder(order.id, { status: '已拒絕' })}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="拒絕此插單"
                                  aria-label="拒絕插單"
                                >
                                  <span aria-hidden="true">✕</span>
                                  <span className="sr-only">拒絕</span>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 統計視圖 */}
      {viewType === 'statistics' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* 總插單數 */}
          <div className="bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-medium text-rose-700 mb-2">總插單數</div>
            <div className="text-3xl font-black text-rose-600">{statistics.total}</div>
          </div>

          {/* 待確認 */}
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-medium text-amber-700 mb-2">待確認</div>
            <div className="text-3xl font-black text-amber-600">{statistics.pending}</div>
          </div>

          {/* 已接受 */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-medium text-blue-700 mb-2">已接受</div>
            <div className="text-3xl font-black text-blue-600">{statistics.approved}</div>
          </div>

          {/* 已拒絕 */}
          <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-medium text-red-700 mb-2">已拒絕</div>
            <div className="text-3xl font-black text-red-600">{statistics.rejected}</div>
          </div>

          {/* 平均處理天數 */}
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 border-2 border-purple-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-sm font-medium text-purple-700 mb-2">平均交期壓縮天數</div>
            <div className="text-3xl font-black text-purple-600">{statistics.avgProcessingDays} 天</div>
          </div>
        </div>
      )}
    </div>
  );
}
